import {
  UNVERIFIED_ADMIN,
  UNVERIFIED_PURCHASER,
  UNVERIFIED_DIY,
  VERIFIED_ADMIN,
  VERIFIED_PURCHASER,
  VERIFIED_USER,
  ROUTES,
} from '../constants/constants';

export const getSvocId = (): string | null => {
  if (typeof window !== 'undefined' && window?.THDCustomer) {
    return window.THDCustomer.default?.svocID ?? null;
  }
  if (typeof window !== 'undefined' && window?.THD_USER_SESSION) {
    return window.THD_USER_SESSION.default?.svocID ?? null;
  }
  return null; // '041FBEB7EE53EAF20S';
};

export const getUserId = (): string | null => {
  if (typeof window !== 'undefined' && window?.THDCustomer) {
    return window.THDCustomer.default?.userID ?? null;
  }
  if (typeof window !== 'undefined' && window?.THD_USER_SESSION) {
    return window.THD_USER_SESSION.default?.userID ?? null;
  }
  return null; // '041FBEB7EE69EAF20U';
};

export const isB2BUser = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    !!window?.THDCustomer?.default?.isB2BCustomer
  );
};

export const getHost = () => {
  return window.location.hostname;
};

export const isB2Badmin = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    window?.THDCustomer?.default?.role === 'ADMIN'
  );
};

export const userRole = () => {
  if (window?.THDCustomer?.default?.role === 'ADMIN') {
    return 'Admin';
  }
  if (window?.THDCustomer?.default?.role === 'PURCHASER') {
    return 'Purchaser';
  }
  return null;
};

export const getUserEmail = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window?.THDCustomer?.default?.email ?? null;
};

export const defineUserType = (
  isB2B: boolean | undefined,
  isAdmin: boolean | undefined,
  isEnrolled: boolean | undefined,
) => {
  if (isB2B && !isAdmin && !isEnrolled) {
    return UNVERIFIED_PURCHASER;
  }
  if (isB2B && isAdmin && !isEnrolled) {
    return UNVERIFIED_ADMIN;
  }
  if (!isB2B && !isEnrolled) {
    return UNVERIFIED_DIY;
  }
  if (isB2B && isEnrolled && !isAdmin) {
    return VERIFIED_PURCHASER;
  }
  if (isB2B && isEnrolled && isAdmin) {
    return VERIFIED_ADMIN;
  }
  if (!isB2B && isEnrolled) {
    return VERIFIED_USER;
  }
  return UNVERIFIED_DIY;
};

export const getAuthorizationToken = (): string | null => {
  if (typeof window !== 'undefined' && window?.THDCustomer) {
    return window.THDCustomer.default?.jwt ?? null;
  }
  if (
    typeof window !== 'undefined' &&
    window?.THD_USER_SESSION &&
    window?.THD_USER
  ) {
    return window.THD_USER_SESSION.default?.jwt ?? null;
  }
  return null;
};

/* Anchor for an FAQ section heading, derived from the heading itself. The
/* sections are ordered differently for enrolled and unenrolled customers, so
/* anything positional - like the accordion ids, which are built from the render
/* index - would point at a different section depending on who is reading. */
export const faqSectionId = (title: string) =>
  `faq-${String(title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;

/* The `code`/`state` pair ID.me leaves on the callback, as the raw `key=value`
/* text it arrived as, or '' on an ordinary page load. Filtered by literal key
/* rather than round-tripped through `URLSearchParams#toString()`, which
/* re-encodes every value under form-urlencoded rules (`%2B` becomes `+`, `~`
/* becomes `%7E`, ...). The BFF seals `state` and replays `code` at token
/* exchange, so both have to survive byte for byte. */
const idmeCallbackQuery = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.search
    .replace(/^\?/, '')
    .split('&')
    .filter((entry) => {
      const key = entry.split('=', 1)[0];
      return key === 'code' || key === 'state';
    })
    .join('&');
};

/* The params ID.me leaves when it sends a customer back without one: `error`
/* and, when it has something to add, `error_description` - alongside the same
/* `state` a successful return carries. */
const IDME_ERROR_PARAMS = ['error', 'error_description', 'state'];

/* Whether this landing is a customer coming back from ID.me's consent screen
/* having declined to share their information with The Home Depot.
/*
/* Read here rather than through the verification component, which deliberately
/* does not answer this: `readCallbackParams()` requires `code` and `state`
/* together, and a decline carries `state` with no `code`. That is the right
/* answer to the question the component asks - there is nothing to exchange - so
/* this is a second question about the same URL, not a looser version of that
/* one. Keeping them apart is what stops a decline being mistaken for a callback
/* worth attempting. */
export const hasIdmeAccessDenied = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    new URLSearchParams(window.location.search).get('error') === 'access_denied'
  );
};

/* Takes ID.me's error redirect off the address bar once it has been read, so a
/* reload does not put the message back up long after the customer has moved on.
/*
/* Filtered as raw `key=value` text rather than round-tripped through
/* `URLSearchParams#toString()`, for the same reason `idmeCallbackQuery` above is
/* and the component's own `clearCallbackParams` is: the round trip re-encodes
/* every surviving entry under form-urlencoded rules (`%2B` becomes `+`, `~`
/* becomes `%7E`), quietly rewriting query text this function has no business
/* touching. The pathname, the hash, and every other entry's exact text survive.
/*
/* `replaceState` rather than a push, so a back/forward to this entry restores
/* the sanitized URL and not the one still carrying the decline.
/*
/* `state` goes with the other two even though nothing here reads it. It arrives
/* orphaned - the authorization endpoint answers with a `code` or an `error` and
/* never both, so there is no exchange whose `state` this could be pulling out
/* from under - and left behind it is a half-callback that `signInRedirectUrl`
/* would go on carrying through a sign-in trip for a flow that is already over. */
export const clearIdmeErrorParams = () => {
  const { pathname, search, hash } = window.location;
  const query = search
    .replace(/^\?/, '')
    .split('&')
    .filter((entry) => !IDME_ERROR_PARAMS.includes(entry.split('=', 1)[0]))
    .join('&');
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${query ? `?${query}` : ''}${hash}`,
  );
};

/* The sign-in URL, carrying an in-flight ID.me verification back with it. A
/* customer whose session lapsed while they were away at ID.me returns to a
/* callback URL; sending them to sign in without `code`/`state` would strand the
/* verification they just earned and make them start over. The military
/* verification component deliberately leaves the pair on the URL when it finds
/* no session to exchange against, waiting for them to come back with it intact.
/*
/* Only those two params are carried - anything else on the query string is
/* dropped, so an ordinary expired-session visit lands back exactly where it
/* does today. The whole return address is encoded once, as a single `redirect`
/* value, so the sign-in page hands back the query string rather than reading it
/* as its own.
/*
/* Split from the navigation below so the address can be asserted on: jsdom
/* seals `window.location`, leaving no way to observe a `replace` call. */
export const signInRedirectUrl = () => {
  const query = idmeCallbackQuery();
  const target = `${ROUTES.landing.url}${query ? `?${query}` : ''}`;
  return `/auth/view/signin?redirect=${encodeURIComponent(target)}`;
};

export const redirectToSignIn = () => {
  window.location.replace(signInRedirectUrl());
};

export const redirectToLearnMore = () => {
  window.location.href = 'http://www.homedepot.com/c/military/';
};

/* An ISO instant that names its zone - `Z` or a `±HH:MM` offset. Without one,
/* `new Date` reads the value in the visitor's own zone. */
const ZONED_INSTANT_PATTERN = /(Z|[+-]\d{2}:\d{2})$/;

/* Epoch milliseconds for a zoned ISO instant, or NaN for anything else. */
const toInstant = (value?: string | null) =>
  value && ZONED_INSTANT_PATTERN.test(value) ? Date.parse(value) : NaN;

/* Whether the Veterans Day Challenge is showable to a member who registered at
/* `registeredAt` (an ISO instant - the enrollment's `verificationDate`).
/* The two boundaries are config values written as ISO instants with their zone
/* (e.g. `2026-11-10T22:00:00-05:00`), so they name a moment in time and no zone
/* is assumed here. The challenge start is inclusive and does double duty: nobody
/* sees the challenge before it, and only a member registered at or before it is
/* eligible. The challenge end is exclusive ("on/after" closes it). Anything
/* missing, unparseable or without a zone hides the challenge. */
export const isVeteransDayChallengeVisible = ({
  registeredAt,
  challengeStart,
  challengeEnd,
  now = new Date(),
}: {
  registeredAt?: string | null;
  challengeStart?: string | null;
  challengeEnd?: string | null;
  now?: Date;
}) => {
  const start = toInstant(challengeStart);
  const end = toInstant(challengeEnd);
  const registered = toInstant(registeredAt);
  if ([start, end, registered].some(Number.isNaN)) {
    return false;
  }
  const current = now.getTime();
  return registered <= start && current >= start && current < end;
};
